/**
 * CR-4865: Delivery-Appeal-style grouped card for a single PO change request.
 *
 * Takes the POChangeLog rows sharing a change_request_ref plus any
 * attachments on the same ref and renders them as:
 *   Header  — #<ref>, status badge, one-line summary
 *   Details — change type, old → new, submitted by, reason, attachments
 *   Status  — reviewed by, decision comment, attachments (when resolved)
 *   History — When / Company / User / Description table
 *
 * This is a pure presentational component; callers pass in the already-
 * grouped rows + attachments list.
 */
import { useState } from 'react';
import { Calendar, CheckCircle, Clock, DollarSign, Edit, Package, Paperclip, User, XCircle, Download, Trash2, Loader2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { ChangelogEntry } from '../common/Changelog';
import { POChangeRequestAttachment, purchasingAPI } from '../../lib/api';
import { getOtifDateVariance, OtifWindow } from '../../utils/otifDateVariance';
import { toast } from 'sonner';
import FileDropZone from '../common/FileDropZone';

export type ChangeRequestStatus = 'open' | 'accepted' | 'declined' | 'cancelled';

// CR-7166: Added 'required_date' for customer-raised date changes.
export interface ChangeRequestGroup {
  ref: number;
  poLineId: number;
  status: ChangeRequestStatus;
  changeType: 'promise_date' | 'required_date' | 'quantity' | 'unit_price' | 'other';
  fieldLabel: string;
  oldValue: string;
  newValue: string;
  submission: ChangelogEntry | null;
  decision: ChangelogEntry | null;
  history: ChangelogEntry[]; // all rows in this group, chronological oldest-first
}

interface ChangeRequestCardProps {
  group: ChangeRequestGroup;
  attachments: POChangeRequestAttachment[];
  canUploadSubmission: boolean;
  canUploadDecision: boolean;
  onAttachmentsChanged?: () => void;
  formatDateTime: (s: string) => string;
  /** CR-5479: PO line ODD (odd_date — the date OTIF uses) + supplier OTIF window so a
   *  promise_date change's new value can be amber/red colour-coded. */
  odd?: string | null;
  otifWindow?: OtifWindow | null;
}

// CR-7166: Added required_date icon (same as promise_date since both are dates).
const FIELD_ICON: Record<ChangeRequestGroup['changeType'], LucideIcon> = {
  promise_date: Calendar,
  required_date: Calendar,
  quantity: Package,
  unit_price: DollarSign,
  other: Edit,
};

const STATUS_BADGE_CONFIG: Record<ChangeRequestStatus, { label: string; className: string; icon: LucideIcon }> = {
  open: { label: 'Pending', className: 'bg-orange-100 text-orange-800 border-orange-200', icon: Clock },
  accepted: { label: 'Accepted', className: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle },
  declined: { label: 'Rejected', className: 'bg-red-100 text-red-800 border-red-200', icon: XCircle },
  cancelled: { label: 'Cancelled', className: 'bg-gray-100 text-gray-700 border-gray-200', icon: XCircle },
};

function StatusBadge({ status }: { status: ChangeRequestStatus }) {
  const c = STATUS_BADGE_CONFIG[status];
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold border ${c.className}`}>
      <Icon className="h-3 w-3" />
      {c.label}
    </span>
  );
}

export default function ChangeRequestCard({
  group,
  attachments,
  canUploadSubmission,
  canUploadDecision,
  onAttachmentsChanged,
  formatDateTime,
  odd,
  otifWindow,
}: ChangeRequestCardProps) {
  const FieldIcon = FIELD_ICON[group.changeType];
  const submissionAttachments = attachments.filter(a => a.side === 'submission');
  const decisionAttachments = attachments.filter(a => a.side === 'decision');
  const [uploadingSide, setUploadingSide] = useState<'submission' | 'decision' | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // CR-5479: for a promise-date change, classify the proposed (new) date
  // against the line's ODD + OTIF window so it renders with the same
  // amber/red + "(+N days late)" indicator as the PO Line Items table.
  // CR-7166: Also apply to required_date changes.
  const promiseVariance = (group.changeType === 'promise_date' || group.changeType === 'required_date')
    ? getOtifDateVariance(group.newValue, odd, otifWindow)
    : null;
  const newValueClass = promiseVariance?.className
    ? `inline-block px-1.5 py-0.5 rounded ${promiseVariance.className}`
    : 'font-semibold';
  const newValueSuffix = promiseVariance?.suffix || '';

  const summary = `${group.fieldLabel}: ${group.oldValue} → ${group.newValue}${newValueSuffix}`;

  const handleUpload = async (side: 'submission' | 'decision', file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size exceeds 10MB limit');
      return;
    }
    setUploadingSide(side);
    try {
      await purchasingAPI.uploadChangeRequestAttachment({
        po_line: group.poLineId,
        change_request_ref: group.ref,
        side,
        file,
      });
      toast.success('Attachment uploaded');
      onAttachmentsChanged?.();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      toast.error(axiosErr.response?.data?.message || 'Failed to upload attachment');
    } finally {
      setUploadingSide(null);
    }
  };

  const handleDownload = async (attachment: POChangeRequestAttachment) => {
    try {
      const response = await purchasingAPI.downloadChangeRequestAttachment(attachment.id);
      const blob = new Blob([response.data], { type: attachment.mime_type || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.file_name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download attachment');
    }
  };

  const handleDelete = async (attachment: POChangeRequestAttachment) => {
    setDeletingId(attachment.id);
    try {
      await purchasingAPI.deleteChangeRequestAttachment(attachment.id);
      toast.success('Attachment removed');
      onAttachmentsChanged?.();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      toast.error(axiosErr.response?.data?.message || 'Failed to delete attachment');
    } finally {
      setDeletingId(null);
    }
  };

  const renderAttachmentList = (list: POChangeRequestAttachment[]) => (
    <ul className="mt-2 space-y-1">
      {list.map(att => (
        <li key={att.id} className="flex items-center gap-2 text-xs">
          <Paperclip className="h-3 w-3 text-muted-foreground flex-shrink-0" />
          <button
            type="button"
            onClick={() => handleDownload(att)}
            className="text-primary hover:underline truncate"
            title={`${att.file_name} (${(att.file_size / 1024).toFixed(1)} KB)`}
          >
            {att.file_name}
          </button>
          <span className="text-muted-foreground">
            — {att.uploaded_by_name || 'Unknown'}{att.uploaded_by_company_name ? ` · ${att.uploaded_by_company_name}` : ''}
          </span>
          <button
            type="button"
            onClick={() => handleDelete(att)}
            disabled={deletingId === att.id}
            className="ml-auto text-muted-foreground hover:text-red-600 disabled:opacity-50"
            title="Remove attachment"
          >
            {deletingId === att.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
          </button>
          <button
            type="button"
            onClick={() => handleDownload(att)}
            className="text-muted-foreground hover:text-primary"
            title="Download"
          >
            <Download className="h-3 w-3" />
          </button>
        </li>
      ))}
    </ul>
  );

  // Customer review 11 Sep (drag and drop everywhere): was an "Attach file"
  // label over a hidden input; now the shared drop zone.
  const renderUploadButton = (side: 'submission' | 'decision') => (
    <FileDropZone
      size="compact"
      accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.gif"
      busy={uploadingSide === side}
      disabled={uploadingSide !== null && uploadingSide !== side}
      onFiles={(files) => void handleUpload(side, files[0])}
      className="w-60 shrink-0"
      data-testid={`change-request-${side}-drop-zone`}
    />
  );

  return (
    <div className="border rounded-lg bg-white shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-2 border-b bg-muted/30">
        <span className="font-mono font-semibold text-sm">#{group.ref}</span>
        <FieldIcon className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm text-muted-foreground truncate flex-1" title={summary}>
          {summary}
        </span>
        <StatusBadge status={group.status} />
      </div>

      {/* Details (submission side) */}
      {group.submission && (
        <div className="p-4 border-b space-y-2">
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Submitted</div>
          <div className="grid gap-2 text-sm md:grid-cols-2">
            <div>
              <span className="text-muted-foreground">Change Type:</span>{' '}
              <span className="font-medium">{group.fieldLabel}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Old → New:</span>{' '}
              <span>{group.oldValue}</span>
              <span className="mx-1 text-muted-foreground">→</span>
              <span className={newValueClass}>{group.newValue}{newValueSuffix}</span>
            </div>
            <div className="flex items-center gap-1">
              <User className="h-3 w-3 text-muted-foreground" />
              <span className="text-muted-foreground">By:</span>{' '}
              <span>{group.submission.changed_by_company ? `${group.submission.changed_by_company} · ` : ''}{group.submission.changed_by}</span>
            </div>
            <div>
              <span className="text-muted-foreground">At:</span>{' '}
              <span>{formatDateTime(group.submission.change_date)}</span>
            </div>
          </div>
          {group.submission.reason && (
            <div className="text-xs text-muted-foreground">
              Reason: <span className="text-foreground">{group.submission.reason}</span>
            </div>
          )}
          <div className="flex items-start justify-between gap-2 pt-1">
            <div className="flex-1">
              <div className="text-xs font-medium text-muted-foreground">
                Submission attachments {submissionAttachments.length > 0 && `(${submissionAttachments.length})`}
              </div>
              {submissionAttachments.length > 0 ? renderAttachmentList(submissionAttachments) : (
                <p className="text-xs text-muted-foreground italic mt-1">No files attached.</p>
              )}
            </div>
            {canUploadSubmission && renderUploadButton('submission')}
          </div>
        </div>
      )}

      {/* Status (decision side) — only when the request has been resolved */}
      {group.decision && group.status !== 'open' && (
        <div className="p-4 border-b space-y-2 bg-muted/10">
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Outcome</div>
          <div className="grid gap-2 text-sm md:grid-cols-2">
            <div className="flex items-center gap-1">
              <User className="h-3 w-3 text-muted-foreground" />
              <span className="text-muted-foreground">Reviewed by:</span>{' '}
              <span>{group.decision.changed_by_company ? `${group.decision.changed_by_company} · ` : ''}{group.decision.changed_by}</span>
            </div>
            <div>
              <span className="text-muted-foreground">At:</span>{' '}
              <span>{formatDateTime(group.decision.change_date)}</span>
            </div>
          </div>
          {group.decision.reason && (
            <div className="text-xs text-muted-foreground">
              Comment: <span className="text-foreground">{group.decision.reason}</span>
            </div>
          )}
          <div className="flex items-start justify-between gap-2 pt-1">
            <div className="flex-1">
              <div className="text-xs font-medium text-muted-foreground">
                Decision attachments {decisionAttachments.length > 0 && `(${decisionAttachments.length})`}
              </div>
              {decisionAttachments.length > 0 ? renderAttachmentList(decisionAttachments) : (
                <p className="text-xs text-muted-foreground italic mt-1">No files attached.</p>
              )}
            </div>
            {canUploadDecision && renderUploadButton('decision')}
          </div>
        </div>
      )}

      {/* History table */}
      {group.history.length > 0 && (
        <div className="px-4 py-3">
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">History</div>
          <table className="w-full text-xs">
            <thead className="text-muted-foreground">
              <tr>
                <th className="text-left font-medium py-1 pr-2">When</th>
                <th className="text-left font-medium py-1 pr-2">Company</th>
                <th className="text-left font-medium py-1 pr-2">User</th>
                <th className="text-left font-medium py-1">Description</th>
              </tr>
            </thead>
            <tbody>
              {group.history.map(entry => (
                <tr key={entry.id} className="border-t">
                  <td className="py-1 pr-2 whitespace-nowrap">{formatDateTime(entry.change_date)}</td>
                  <td className="py-1 pr-2">{entry.changed_by_company || '—'}</td>
                  <td className="py-1 pr-2">{entry.changed_by}</td>
                  <td className="py-1">{entry.reason || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
